import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SitemapGenerator from './components/SitemapGenerator';
import ContentPreview from './components/ContentPreview';
import DesignTemplate from './components/DesignTemplate';
import DownloadOutput from './components/DownloadOutput';
import WebsitePreview from './components/WebsitePreview';
import LogoutButton from '../../components/LogoutButton';
import WebsiteBuilderSidebar from './components/WebsiteBuilderSidebar';
// import { initialTheme, pageContents, siteMapNodes, type SiteNode, type ThemeData, type PageContent } from '@/mocks/websiteBuilder';
import { initialTheme, pageContents, siteMapNodes, type SiteNode, type ThemeData, type PageContent } from '../../mocks/websiteBuilder';
// import api from '@/services/api';
import api from '../../services/api';
import { WEBSITE_BUILDER_STEPS } from '../../constants/telemetryModules';
import { subscriptionService } from '../../services/subscriptionService';
import { usePageTracking } from '../../hooks/usePageTracking';
import { useTelemetry } from '../../hooks/useTelemetry';
import { MODULES } from '../../telemetry/constants/modules';
import { ACTIONS } from '../../telemetry/constants/actions';
import { CREDIT_COSTS, CREDIT_THRESHOLDS } from '../../config/creditCosts';
import LowCreditModal from '../../components/LowCreditModal';
import BrandNotificationBell from '../../components/BrandNotificationBell';
import CreditsWidget from '../../components/CreditsWidget';
import { creditService } from '../../services/creditService';
import CreditDeductionConfirmModal from '../../components/CreditDeductionConfirmModal';
import { useCredits } from '../../hooks/useCredits';
import { usePlatformNotifications } from '../../hooks/usePlatformNotifications';

export type GlobalContactInfo = {
  email: string;
  phone: string;
  location: string;
  description: string;
  socials: Record<string, string>;
};

export default function WebsiteBuilder() {
  const [currentStep, setCurrentStep] = useState(1);

  // Low Credit Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCredits, setModalCredits] = useState(0);
  const [modalRequired, setModalRequired] = useState(0);
  const [modalIsBlocker, setModalIsBlocker] = useState(false);

  // Upfront Charge & Locking States
  const [projectState, setProjectState] = useState<'None' | 'Draft' | 'Downloaded' | 'checking'>('checking');
  const [isExported, setIsExported] = useState(false);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showNoSessionModal, setShowNoSessionModal] = useState(false);
  const [showDeductionConfirmModal, setShowDeductionConfirmModal] = useState(false);
  const [isDeducting, setIsDeducting] = useState(false);
  const [pendingAction, setPendingAction] = useState<'new-site' | 'start-fresh' | 'unlock' | null>(null);

  const { invalidateCredits } = useCredits();
  const { notificationsQuery, unreadCountQuery } = usePlatformNotifications();

  const checkCreditsForAction = async (required: number): Promise<{ allowed: boolean; current: number }> => {
    try {
      const bal = await creditService.getBalance();
      const current = bal.total_credits_balance;
      if (current < required) {
        setModalCredits(current);
        setModalRequired(required);
        setModalIsBlocker(true);
        setModalOpen(true);
        return { allowed: false, current };
      }
      return { allowed: true, current };
    } catch (err) {
      console.error("Failed to check credits:", err);
    }
    return { allowed: true, current: 0 };
  };

  const checkLowCreditWarning = async (current: number) => {
    if (current < CREDIT_THRESHOLDS.LOW_CREDIT_WARNING) {
      setModalCredits(current);
      setModalIsBlocker(false);
      setModalOpen(true);
    }
  };
  usePageTracking({
    moduleName: 'Website Builder',
    submoduleName: WEBSITE_BUILDER_STEPS[currentStep] || 'Sitemap',
    routePath: `/website-builder#step-${currentStep}`,
  });
  const { trackAction } = useTelemetry({ module: MODULES.WEBSITE_BUILDER, submodule: 'Website Builder' });
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('t1');
  const [theme, setTheme] = useState<ThemeData>(initialTheme);
  const [pages, setPages] = useState<PageContent[]>(pageContents);
  const [activePageId, setActivePageId] = useState<string>('home');
  const [brandTheme, setBrandTheme] = useState<Partial<ThemeData> | null>(null);

  const [globalLoading, setGlobalLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');

  const [showSessionModal, setShowSessionModal] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  const location = useLocation();
  const initialBrandName = (location.state as { brand?: string })?.brand || 'Glamac';
  const [brandName, setBrandName] = useState(initialBrandName);
  const [brandLogo, setBrandLogo] = useState<string | null>(null);
  const [globalContactInfo, setGlobalContactInfo] = useState<GlobalContactInfo>({
    email: '',
    phone: '',
    location: '',
    description: '',
    socials: {}
  });

  // Save & Auto-Save Tracking
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'unsaved'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const latestPagesRef = useRef(pages);
  const latestContactRef = useRef(globalContactInfo);

  useEffect(() => {
    latestPagesRef.current = pages;
  }, [pages]);

  useEffect(() => {
    latestContactRef.current = globalContactInfo;
  }, [globalContactInfo]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) return;
        const token = localStorage.getItem('access_token');
        const resJson = await api.getWebsiteProfile(userId);
        const data = resJson.data || resJson;
        const profileData = data.business_profile || data;
        const productsData = data.products || [];
        const servicesData = data.services || [];

        if (profileData) {
          if (profileData.business_name) {
            setBrandName(profileData.business_name);
          }
          if (profileData.logo_url) {
            setBrandLogo(profileData.logo_url.replace(/"/g, ''));
          }
          setGlobalContactInfo(prev => {
            const next = { ...prev };
            // Only accept plaintext values — reject encrypted tokens (gAAAAAB prefix)
            const isEncrypted = (v: string) => v && v.startsWith('gAAAAAB');
            if (profileData.email && !isEncrypted(profileData.email)) next.email = profileData.email;
            if (profileData.phone_number && !isEncrypted(profileData.phone_number)) next.phone = profileData.phone_number.replace(/-/g, '');
            if (profileData.location || profileData.city || profileData.state || profileData.country) {
              const parts = [profileData.location, profileData.city, profileData.state, profileData.country].filter((v: string) => v && !isEncrypted(v));
              if (parts.length > 0) next.location = parts.join(', ');
            }
            return next;
          });

          const toTitleCase = (str: string) => {
            if (!str) return '';
            return str.replace(
              /\w\S*/g,
              (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
            );
          };

          const cleanArray = (arr: any[]) => {
            if (!Array.isArray(arr)) return [];
            return arr.map(item => {
              let val = '';
              if (typeof item === 'string') val = item;
              else if (typeof item === 'object' && item !== null) {
                val = item.name || item.title || item.product_name || item.service_name || '';
              }
              return toTitleCase(val.trim());
            }).filter(item => item.length > 0);
          };

          const servicesList = cleanArray(servicesData);
          const productsList = cleanArray(productsData);

          // Dynamically adapt sitemap to the business offering type from onboarding
          const hasProducts = productsList.length > 0;
          const hasServices = servicesList.length > 0;

          let dynamicOfferingType = 'both';
          if (hasProducts && !hasServices) dynamicOfferingType = 'product';
          if (hasServices && !hasProducts) dynamicOfferingType = 'service';

          setNodes(() => {
            let initialNodes = JSON.parse(JSON.stringify(siteMapNodes));
            if (dynamicOfferingType === 'product') {
              initialNodes = initialNodes.filter((n: SiteNode) => n.id !== 'services');
            } else if (dynamicOfferingType === 'service') {
              initialNodes = initialNodes.filter((n: SiteNode) => n.id !== 'products');
            }

            initialNodes = initialNodes.map((node: SiteNode) => {
              if (node.id === 'services' || node.id === 'products') {
                delete node.children;
              }
              return node;
            });

            return initialNodes;
          });

          // Hydrate Builder Theme from Profile Branding
          if (profileData.primary_color || profileData.secondary_color || profileData.primary_font || profileData.secondary_font) {
            const nextBrandTheme: Partial<ThemeData> = {};
            if (profileData.primary_color) {
              nextBrandTheme.primary = profileData.primary_color;
              nextBrandTheme.cta = profileData.primary_color;
            }
            if (profileData.secondary_color) {
              nextBrandTheme.accent = profileData.secondary_color;
            }
            if (profileData.primary_font) {
              nextBrandTheme.headingFont = profileData.primary_font;
            }
            if (profileData.secondary_font) {
              nextBrandTheme.bodyFont = profileData.secondary_font;
            }
            setBrandTheme(nextBrandTheme);
            setTheme(prev => ({ ...prev, ...nextBrandTheme }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch onboarding profile:", err);
      }
    };

    const checkSession = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        const stateRes = await api.getWebsiteProjectState();
        const state = stateRes.data?.state; // 'Draft' | 'Downloaded' | 'None'
        setProjectState(state || 'None');
        
        if (state === 'Downloaded') {
          setIsExported(true);
          setShowSessionModal(true);
        } else if (state === 'None') {
          setShowNoSessionModal(true);
        } else if (state === 'Draft') {
          const res = await api.checkWebsiteSession();
          if (res.status === 'success' && res.data?.session_exists) {
            setShowSessionModal(true);
          }
        }
      } catch (err) {
        console.error("Failed to check session:", err);
      } finally {
        setIsCheckingSession(false);
      }
    };

    fetchProfile();
    checkSession();
  }, []);

  const triggerChargeConfirm = async (action: 'start-fresh' | 'new-site' | 'unlock') => {
    try {
      const bal = await creditService.getBalance();
      const current = bal.total_credits_balance;
      const required = CREDIT_COSTS.FULL_WEBSITE || 5000;
      
      if (current < required) {
        setModalCredits(current);
        setModalRequired(required);
        setModalIsBlocker(true);
        setModalOpen(true);
        return;
      }
      
      setModalCredits(current);
      setPendingAction(action);
      setShowDeductionConfirmModal(true);
    } catch (err) {
      console.error("Failed to check credits for charge:", err);
    }
  };

  const handleConfirmDeduction = async () => {
    setIsDeducting(true);
    try {
      if (pendingAction === 'unlock') {
        const res = await api.unlockWebsiteBuilder();
        if (res.status === 'success') {
          setIsExported(false);
          setShowDeductionConfirmModal(false);
          
          await invalidateCredits();
          notificationsQuery.refetch();
          unreadCountQuery.refetch();
        }
      } else {
        const res = await api.chargeWebsiteBuilder();
        if (res.status === 'success') {
          setShowDeductionConfirmModal(false);
          window.location.reload();
        }
      }
    } catch (err) {
      console.error("Deduction failed:", err);
      alert("Failed to charge credits. Please try again.");
    } finally {
      setIsDeducting(false);
    }
  };





  const [nodes, setNodes] = useState<SiteNode[]>(() => {
    const state = location.state as { brand?: string; businessType?: string; offeringType?: string };
    const offeringType = state?.offeringType?.toLowerCase() || 'both'; // fallback before API loads

    let initialNodes = JSON.parse(JSON.stringify(siteMapNodes));

    if (offeringType === 'product') {
      initialNodes = initialNodes.filter((n: SiteNode) => n.id !== 'services');
    } else if (offeringType === 'service') {
      initialNodes = initialNodes.filter((n: SiteNode) => n.id !== 'products');
    }

    initialNodes = initialNodes.map((node: SiteNode) => {
      if (node.id === 'services' || node.id === 'products') {
        delete node.children;
      }
      return node;
    });

    return initialNodes;
  });
  const navigate = useNavigate();

  const deduplicatePages = (pagesList: PageContent[]): PageContent[] => {
    if (!Array.isArray(pagesList)) return [];
    const map = new Map<string, PageContent>();

    for (const page of pagesList) {
      let cleanId = page.pageId;
      if (cleanId === 'about-us') cleanId = 'about';
      if (cleanId === 'contact-us') cleanId = 'contact';
      if (cleanId === 'service-landing') cleanId = 'services';
      if (cleanId === 'product-landing') cleanId = 'products';

      let cleanName = page.pageName;
      if (cleanId === 'about') cleanName = 'About Us';
      if (cleanId === 'contact') cleanName = 'Contact Us';
      if (cleanId === 'services') cleanName = 'Services';
      if (cleanId === 'products') cleanName = 'Products';
      if (cleanId === 'home') cleanName = 'Home';
      if (cleanId === 'blog') cleanName = 'Blog';
      if (cleanId === 'gallery') cleanName = 'Gallery';

      const normalizedPage: PageContent = {
        ...page,
        pageId: cleanId,
        pageName: cleanName
      };

      if (map.has(cleanId)) {
        const existing = map.get(cleanId)!;
        if (normalizedPage.status === 'Generated' && existing.status !== 'Generated') {
          map.set(cleanId, normalizedPage);
        }
      } else {
        map.set(cleanId, normalizedPage);
      }
    }

    return Array.from(map.values());
  };

  const getDerivedPages = () => {
    // Only include top-level enabled nodes (ignore children)
    const activeNodes = nodes.filter(n => n.enabled);

    const derived = activeNodes.map(n => {
      // Look for existing page content either from state or default template mocks
      const existing = pages.find(p => p.pageId === n.id || p.pageName.toLowerCase() === n.name.toLowerCase())
                    || pageContents.find(p => p.pageId === n.id || p.pageName.toLowerCase() === n.name.toLowerCase());

      if (existing) {
        return {
          ...existing,
          pageId: n.id,
          pageName: n.name,
          sections: existing.sections.map(s => ({
            ...s,
            content: Array.isArray(s.content) ? s.content : (s.content || '')
          }))
        };
      }

      const descLower = (n.description || '').toLowerCase().trim();
      const nameLower = (n.name || '').toLowerCase().trim();
      const combined = `${nameLower} ${descLower}`;

      const customSections: any[] = [
        {
          id: `hero_${n.id}`,
          type: 'hero',
          title: 'Hero Banner',
          enabled: true,
          content: []
        }
      ];

      if (descLower) {
        if (/career|job|opening|hiring|recruit|culture|employee|growth opportunit/.test(combined)) {
          if (/culture|benefit|perk|why us|growth|value/.test(combined)) {
            customSections.push({
              id: `usp_${n.id}`,
              type: 'usp',
              title: 'Culture & Benefits',
              enabled: true,
              content: []
            });
          }
          if (/job|opening|position|role|vacancy|hiring|opportunit/.test(combined)) {
            customSections.push({
              id: `grid_${n.id}`,
              type: 'grid',
              title: 'Current Openings',
              enabled: true,
              content: []
            });
          }
          if (/culture|story|vision|environment|workplace|life at/.test(combined)) {
            customSections.push({
              id: `story_${n.id}`,
              type: 'story',
              title: 'Life at Our Company',
              enabled: true,
              content: ''
            });
          }
          if (/apply|application|join|process|contact|submit|resume|cv/.test(combined)) {
            customSections.push({
              id: `contactForm_${n.id}`,
              type: 'contactForm',
              title: 'Application Form',
              enabled: true,
              content: ''
            });
          }
          if (customSections.length === 1) {
            customSections.push(
              { id: `usp_${n.id}`, type: 'usp', title: 'Culture & Benefits', enabled: true, content: [] },
              { id: `grid_${n.id}`, type: 'grid', title: 'Current Openings', enabled: true, content: [] },
              { id: `story_${n.id}`, type: 'story', title: 'About Working Here', enabled: true, content: '' },
              { id: `contactForm_${n.id}`, type: 'contactForm', title: 'Apply Now', enabled: true, content: '' }
            );
          }
        } else if (/price|pricing|plan|tier|subscription|cost|fee|package/.test(combined)) {
          customSections.push({
            id: `grid_${n.id}`,
            type: 'grid',
            title: 'Pricing Plans',
            enabled: true,
            content: []
          });
          customSections.push({
            id: `faq_${n.id}`,
            type: 'faq',
            title: 'Frequently Asked Questions',
            enabled: true,
            content: []
          });
        } else if (/team|board|leadership|staff|people|executiv|founder/.test(combined)) {
          customSections.push({
            id: `team_${n.id}`,
            type: 'team',
            title: 'Our Team',
            enabled: true,
            content: []
          });
          customSections.push({
            id: `story_${n.id}`,
            type: 'story',
            title: 'About Our Team',
            enabled: true,
            content: ''
          });
        } else if (/faq|question|help|support|guide/.test(combined)) {
          customSections.push({
            id: `faq_${n.id}`,
            type: 'faq',
            title: 'FAQ',
            enabled: true,
            content: []
          });
          customSections.push({
            id: `contactForm_${n.id}`,
            type: 'contactForm',
            title: 'Get Help',
            enabled: true,
            content: ''
          });
        } else {
          customSections.push({
            id: `story_${n.id}`,
            type: 'story',
            title: 'Main Content',
            enabled: true,
            content: ''
          });
          customSections.push({
            id: `grid_${n.id}`,
            type: 'grid',
            title: 'Highlights',
            enabled: true,
            content: []
          });
        }
      } else {
        // Fallback for custom page with NO description (current behavior persists)
        customSections.push({
          id: `story_${n.id}`,
          type: 'story',
          title: 'Main Content',
          enabled: true,
          content: ''
        });
        if (n.id === 'contact') {
          customSections.push(
            {
              id: `contactInfo_${n.id}`,
              type: 'contactInfo',
              title: 'Contact Information',
              enabled: true,
              content: ''
            },
            {
              id: `contactForm_${n.id}`,
              type: 'contactForm',
              title: 'Contact Form',
              enabled: true,
              content: ''
            }
          );
        }
      }

      return {
        pageId: n.id,
        pageName: n.name,
        status: 'Pending',
        sections: customSections
      };
    }) as PageContent[];

    return deduplicatePages(derived);
  };

  const handleStepChange = async (step: number) => {
    if (isExported) {
      setCurrentStep(step);
      return;
    }
    if (step === 2 && currentStep === 1) {
      await processStep1To2();
    } else if (currentStep === 2 && step !== 2) {
      await saveCurrentContent();
      if (step <= currentStep + 1) setCurrentStep(step);
    } else if (step <= currentStep + 1) {
      setCurrentStep(step);
    }
  };

  const handleManualSave = async (overridePages?: PageContent[], overrideContact?: GlobalContactInfo) => {
    if (isExported) return;
    const targetPages = overridePages || latestPagesRef.current;
    const targetContact = overrideContact || latestContactRef.current;
    const dedupedPages = deduplicatePages(targetPages);

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    setSaveStatus('saving');
    try {
      trackAction(ACTIONS.EDIT_WEBSITE, 'Save Website Content', { pages_count: dedupedPages.length });
      await api.saveContent(dedupedPages, targetContact);
      setSaveStatus('saved');
      setLastSavedAt(new Date());
    } catch (err) {
      console.error("Save content failed:", err);
      setSaveStatus('unsaved');
    }
  };

  const saveCurrentContent = async () => {
    try {
      setGlobalLoading(true);
      setLoadingText('Saving your changes...');
      await handleManualSave(pages, globalContactInfo);
    } catch (err) {
      console.error(err);
    } finally {
      setGlobalLoading(false);
    }
  };

  // 5-second Debounced Auto-Save
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    if (currentStep < 2 || isExported) {
      return;
    }

    setSaveStatus('unsaved');

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(async () => {
      await handleManualSave(latestPagesRef.current, latestContactRef.current);
    }, 5000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [pages, globalContactInfo, currentStep, isExported]);

  // Warn on tab close / reload if unsaved changes exist
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (saveStatus === 'unsaved' || saveStatus === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveStatus]);

  const processStep1To2 = async () => {
    // Only check credits upfront if starting a new project completely from scratch (projectState is 'None')
    if (projectState === 'None') {
      const checkRes = await checkCreditsForAction(CREDIT_COSTS.FULL_WEBSITE);
      if (!checkRes.allowed) return;
    }

    try {
      setGlobalLoading(true);

      // 1. Initialize Project with nodes in the exact ordered sequence
      setLoadingText('Initializing project structure...');
      trackAction(ACTIONS.GENERATE_WEBSITE, 'Initialize Website Project', { nodes_count: nodes.length });
      await api.initializeWebsite(nodes);

      // 2. Fetch the true state from the backend
      setLoadingText('Preparing content canvas...');
      const apiRes = await api.getPages();
      const truePages = deduplicatePages(apiRes.pages || []);
      if (apiRes.globalContactInfo) {
        setGlobalContactInfo(prev => ({ ...prev, ...apiRes.globalContactInfo }));
      }

      // Sort fetched backend pages according to activeNodes order
      const activeNodes = nodes.filter(n => n.enabled);
      const orderMap = new Map(activeNodes.map((n, idx) => [n.id, idx]));
      const sortedTruePages = Array.isArray(truePages) && truePages.length > 0
        ? [...truePages].sort((a, b) => {
            const orderA = orderMap.has(a.pageId) ? orderMap.get(a.pageId)! : 999;
            const orderB = orderMap.has(b.pageId) ? orderMap.get(b.pageId)! : 999;
            return orderA - orderB;
          })
        : [];

      setPages(deduplicatePages(sortedTruePages.length > 0 ? sortedTruePages : getDerivedPages()));
      setCurrentStep(2);
      // Check low credit warning with latest credits
      try {
        const bal = await creditService.getBalance();
        await checkLowCreditWarning(bal.total_credits_balance);
      } catch (err) {
        console.error("Failed to check warning:", err);
      }
    } catch (err) {
      console.error("Initialization failed, falling back to local generation", err);
      setPages(deduplicatePages(getDerivedPages()));
      setCurrentStep(2);
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleStartFresh = async () => {
    if (isExported) {
      setShowSessionModal(false);
      triggerChargeConfirm('start-fresh');
      return;
    }
    setGlobalLoading(true);
    setLoadingText('Starting fresh...');
    try {
      await api.startFreshWebsite();
      setShowSessionModal(false);
    } catch (err) {
      console.error("Start fresh failed:", err);
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleRestoreSession = async () => {
    setGlobalLoading(true);
    setLoadingText('Restoring your session...');
    try {
      const apiRes = await api.getPages();
      const fetchedPages = deduplicatePages(apiRes.pages || []);
      if (apiRes.globalContactInfo) {
        setGlobalContactInfo(prev => ({ ...prev, ...apiRes.globalContactInfo }));
      }

      // Inject contactInfo and contactForm if missing in older sessions
      const patchedPages = fetchedPages.map((p: PageContent) => {
        let sections = [...p.sections];

        if (p.pageId === 'contact') {
          if (!sections.some(s => s.type === 'contactInfo')) {
            sections.push({
              id: `contactInfo_${p.pageId}`,
              type: 'contactInfo',
              title: 'Contact Information',
              enabled: true,
              content: JSON.stringify({ description: 'Reach out to us directly' }),
              editing: false
            });
          }
          if (!sections.some(s => s.type === 'contactForm')) {
            sections.push({
              id: `contactForm_${p.pageId}`,
              type: 'contactForm',
              title: 'Contact Form',
              enabled: true,
              content: JSON.stringify({ heading: 'Send a Message', subtext: 'We will respond within 24 hours' }),
              editing: false
            });
          }
        } else {
          // Remove legacy contactInfo and contactForm from any non-contact page
          sections = sections.filter(s => s.type !== 'contactInfo' && s.type !== 'contactForm');
        }

        return { ...p, sections };
      });

      const finalDedupedPages = deduplicatePages(patchedPages);
      setPages(finalDedupedPages);

      if (finalDedupedPages.length > 0) {
        setNodes(prev => {
          const restoredIds = finalDedupedPages.map(p => p.pageId);
          const orderedNodes = finalDedupedPages.map(p => {
            const existing = prev.find(n => n.id === p.pageId);
            return existing || {
              id: p.pageId,
              name: p.pageName,
              description: 'Custom page',
              enabled: true,
              status: ['home', 'about', 'services', 'products', 'blog', 'gallery', 'contact'].includes(p.pageId) ? 'default' : 'optional',
              children: []
            };
          });
          const remaining = prev.filter(n => !restoredIds.includes(n.id)).map(n => ({ ...n, enabled: false }));
          return [...orderedNodes, ...remaining];
        });
      }

      setCurrentStep(2);
      setShowSessionModal(false);
    } catch (err) {
      console.error("Restore session failed:", err);
    } finally {
      setGlobalLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <SitemapGenerator nodes={nodes} setNodes={setNodes} isExported={isExported} onRequestUnlock={() => triggerChargeConfirm('unlock')} />;
      case 2:
        return (
          <ContentPreview
            pages={pages}
            onPagesChange={setPages}
            brandName={brandName}
            globalContactInfo={globalContactInfo}
            setGlobalContactInfo={setGlobalContactInfo}
            isExported={isExported}
            onRequestUnlock={() => triggerChargeConfirm('unlock')}
            activePageId={activePageId}
            onActivePageIdChange={setActivePageId}
            onManualSave={handleManualSave}
            saveStatus={saveStatus}
          />
        );
      case 3:
        return (
          <DesignTemplate
            selectedTemplate={selectedTemplate}
            onSelectTemplate={setSelectedTemplate}
            theme={theme}
            onThemeChange={setTheme}
            onShowPreview={() => setShowPreview(true)}
            brandName={brandName}
            onBrandNameChange={setBrandName}
            brandLogo={brandLogo}
            onBrandLogoChange={setBrandLogo}
            globalContactInfo={globalContactInfo}
            pages={pages}
            brandTheme={brandTheme}
            isExported={isExported}
            onRequestUnlock={() => triggerChargeConfirm('unlock')}
          />
        );
      case 4:
        return <DownloadOutput pages={pages} theme={theme} brandName={brandName} brandLogo={brandLogo} globalContactInfo={globalContactInfo} />;
      default:
        return <SitemapGenerator nodes={nodes} setNodes={setNodes} isExported={isExported} onRequestUnlock={() => triggerChargeConfirm('unlock')} />;
    }
  };

  const handleNext = async () => {
    if (isExported) {
      if (currentStep < 4) setCurrentStep(curr => curr + 1);
      return;
    }
    if (currentStep === 1) {
      await processStep1To2();
    } else if (currentStep === 2) {
      await saveCurrentContent();
      setCurrentStep(3);
    } else {
      setCurrentStep(curr => curr + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-[#f5f3ef] flex font-body relative" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {showSessionModal && (
        <div className="absolute inset-0 bg-black/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in text-center">
            <div className="w-16 h-16 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-history-line text-2xl text-navy"></i>
            </div>
            <h3 className="font-serif text-2xl text-navy mb-2">Previous Session Found</h3>
            <p className="text-gray-600 mb-8">
              We found your previous website draft. Would you like to restore it to save time, or start completely fresh?
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleStartFresh}
                className="px-6 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={handleRestoreSession}
                className="px-6 py-2.5 bg-[#0859b8] text-white font-medium rounded-lg hover:bg-[#0859b8]/90 transition-colors shadow-lg shadow-[#0859b8]/20 flex items-center gap-2"
              >
                <i className="ri-refresh-line"></i>
                Restore Session
              </button>
            </div>
          </div>
        </div>
      )}

      {showLockedModal && (
        <div className="absolute inset-0 bg-black/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-2xl text-red-500"></i>
            </div>
            <h3 className="font-serif text-2xl text-navy mb-2">Project Locked</h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed">
              This website project has been downloaded and is locked permanently. You cannot make any further changes to it. To make changes or build a new website, you must start fresh (costs 5,000 credits).
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => triggerChargeConfirm('start-fresh')}
                className="w-full px-6 py-2.5 bg-[#0859b8] text-white font-medium rounded-lg hover:bg-[#0859b8]/90 transition-colors shadow-lg shadow-[#0859b8]/20 flex items-center justify-center gap-2"
              >
                <i className="ri-refresh-line"></i>
                Start Fresh (Cost: 5,000 credits)
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/marketing-dashboard')}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Go Home
                </button>
                <button
                  onClick={() => {
                    setShowLockedModal(false);
                    setCurrentStep(4);
                  }}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  View Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNoSessionModal && (
        <div className="absolute inset-0 bg-black/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in text-center">
            <div className="w-16 h-16 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-information-line text-2xl text-navy"></i>
            </div>
            <h3 className="font-serif text-2xl text-navy mb-2">Build New Website</h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed">
              Creating a new website requires 5,000 credits, charged upfront. Top up your balance or proceed to get started.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/marketing-dashboard')}
                className="px-6 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => triggerChargeConfirm('new-site')}
                className="px-6 py-2.5 bg-[#0859b8] text-white font-medium rounded-lg hover:bg-[#0859b8]/90 transition-colors shadow-lg shadow-[#0859b8]/20 flex items-center gap-2"
              >
                Pay & Start Building
              </button>
            </div>
          </div>
        </div>
      )}

      <CreditDeductionConfirmModal
        isOpen={showDeductionConfirmModal}
        onClose={() => setShowDeductionConfirmModal(false)}
        onConfirm={handleConfirmDeduction}
        currentCredits={modalCredits}
        deductCredits={CREDIT_COSTS.FULL_WEBSITE || 5000}
        isProcessing={isDeducting}
      />

      {isCheckingSession && (
        <div className="fixed inset-0 bg-white/70 z-[100] flex flex-col items-center justify-center backdrop-blur-sm">
          <div className="w-12 h-12 border-4 border-[#0859b8]/20 border-t-[#0859b8] rounded-full animate-spin"></div>
          <p className="mt-4 font-serif text-lg text-navy animate-pulse">Checking for previous session...</p>
        </div>
      )}

      {globalLoading && (
        <div className="fixed inset-0 bg-white/70 z-[100] flex flex-col items-center justify-center backdrop-blur-sm">
          <div className="w-12 h-12 border-4 border-[#0859b8]/20 border-t-[#0859b8] rounded-full animate-spin"></div>
          <p className="mt-4 font-serif text-lg text-navy animate-pulse">{loadingText}</p>
        </div>
      )}
      {/* Left Sidebar */}
      <WebsiteBuilderSidebar currentStep={currentStep} onStepChange={handleStepChange} isExported={isExported} />

      {/* Main Canvas */}
      <main className="flex-1 flex flex-col min-w-0 h-full">
        {/* Top Bar */}
        <header className="h-[88px] bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <button
              onClick={() => navigate('/marketing-dashboard')}
              className="hover:text-[#0859b8] transition-colors cursor-pointer"
            >
              Home
            </button>
            <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
            <span className="text-navy font-medium flex items-center gap-2">
              Website Builder
            </span>
          </div>

          <div className="flex items-center gap-3">
            <BrandNotificationBell />

            {/* Credits Widget */}
            <CreditsWidget />

            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer group"
              title="Profile"
            >
              {brandLogo ? (

                <div className="w-8 h-8 rounded-full overflow-hidden bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">

                  <img
                    src={brandLogo.replace(/"/g, '')}
                    alt="Business Logo"
                    className="w-full h-full object-cover"
                  />

                </div>

              ) : (

                <div className="w-8 h-8 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">

                  <span className="text-navy font-semibold text-sm">
                    {brandName.charAt(0).toUpperCase()}
                  </span>

                </div>

              )}
              <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 whitespace-nowrap" title="Profile">
                Profile
              </span>
              <i className="ri-arrow-right-s-line text-gray-400 text-base"></i>
            </button>
            <LogoutButton
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all"
            >
              <i className="ri-logout-box-r-line text-[18px] text-slate-600"></i>

              <span className="hidden lg:inline text-sm font-semibold text-slate-700">
                Logout
              </span>
            </LogoutButton>
            <button
              onClick={() => currentStep > 1 && setShowPreview(true)}
              disabled={currentStep === 1}
              className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap ${currentStep === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0859b8] hover:bg-[#0859b8]/90 text-white cursor-pointer'}`}
            >
              <i className="ri-eye-line"></i>
              Preview Output
            </button>
          </div>
        </header>

        {/* Active Step Content */}
        <div className="flex-1 px-6 pb-6 pt-6 overflow-hidden flex flex-col bg-[#fdfdfc]">
          {renderStep()}
        </div>

        {/* Bottom Action Bar */}
        {currentStep < 4 && (
          <div className="h-16 bg-white border-t border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${currentStep === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <i className="ri-arrow-left-line"></i>
              Back
            </button>

            {/* Center Area: Auto-Save Status & Save Progress (Step 2 & 3) */}
            {currentStep >= 2 && !isExported && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {saveStatus === 'saving' && (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-[#0859b8]/30 border-t-[#0859b8] rounded-full animate-spin"></div>
                      <span className="text-[#0859b8] font-medium">Saving changes...</span>
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-sm"></i>
                      <span className="text-emerald-700 font-medium">
                        All changes saved {lastSavedAt ? `(${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })})` : ''}
                      </span>
                    </>
                  )}
                  {saveStatus === 'unsaved' && (
                    <>
                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                      <span className="text-amber-700 font-medium">Unsaved changes (auto-saving in 5s)</span>
                    </>
                  )}
                  {saveStatus === 'idle' && (
                    <>
                      <i className="ri-checkbox-circle-line text-gray-400 text-sm"></i>
                      <span className="text-gray-500">All changes saved</span>
                    </>
                  )}
                </div>

                <button
                  onClick={() => handleManualSave()}
                  disabled={saveStatus === 'saving'}
                  title="Save current progress"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-save-3-line text-slate-600 text-sm"></i>
                  <span>Save Progress</span>
                </button>
              </div>
            )}

            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">
                Step {currentStep} of 4
              </span>
              <button
                onClick={handleNext}
                className="flex items-center gap-2 bg-[#0859b8] hover:bg-[#0859b8]/90 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {currentStep === 3 ? 'Finish & Download' : 'Continue'}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        )}
        {/* Website Preview Overlay */}
        {showPreview && <WebsitePreview onClose={() => setShowPreview(false)} theme={theme} pages={pages} brandName={brandName} brandLogo={brandLogo} globalContactInfo={globalContactInfo} initialPageId={activePageId} />}
      </main>
      <LowCreditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        currentCredits={modalCredits}
        requiredCredits={modalRequired}
        isBlocker={modalIsBlocker}
      />
    </div>
  );
}
